import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Logo from "../Assets/Navigation/Logo.png";
import { getReservationById } from "../Services/reservationService";
import IconButton from "@mui/material/IconButton";
import Refresh from "@mui/icons-material/Refresh";

const STATUS_MAP = {
  0: { label: "Pending", labelAr: "قيد الانتظار", color: "#ed6c02", bg: "#fff3e0" },
  1: { label: "Confirmed", labelAr: "مؤكد", color: "#2e7d32", bg: "#e8f5e9" },
  2: { label: "Purchased", labelAr: "تم الشراء", color: "#1565c0", bg: "#e3f2fd" },
  3: { label: "Cancelled", labelAr: "ملغي", color: "#d32f2f", bg: "#fbe9e7" },
};

const UNIT_TYPE_MAP = {
  0: { label: "Medical Clinic", labelAr: "عيادة طبية" },
  1: { label: "Shop", labelAr: "متجر" },
  2: { label: "Office", labelAr: "مكتب" },
  3: { label: "Hotel Apartment", labelAr: "وحدة فندقية" },
};

const PageWrapper = styled(Box)({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  padding: "24px 0",
  direction: "ltr",
});

const StyledPaper = styled(Paper)({
  maxWidth: 520,
  margin: "0 auto",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
});

const Header = styled(Box)({
  background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
  color: "#fff",
  padding: "32px 24px 24px",
  textAlign: "center",
});

const Section = styled(Box)({
  padding: "16px 24px",
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "0.95rem",
  color: "#1a237e",
  marginBottom: 12,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});
SectionTitle.defaultProps = { component: "div" };

const FieldRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "6px 0",
  "& .label": {
    color: "#666",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  "& .value": {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#222",
    textAlign: "right",
  },
});

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatCurrency(value) {
  if (value == null) return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";
  return `EGP ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Map PascalCase API response to camelCase so UI can read unit/client/payment fields
function normalizeReservationResponse(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const map = {
    Id: "id",
    Status: "status",
    ReservationDate: "reservationDate",
    RequiredApprovalCount: "requiredApprovalCount",
    IsApprovedByCurrentUser: "isApprovedByCurrentUser",
    ClientId: "clientId",
    ClientName: "clientName",
    ClientEmail: "clientEmail",
    ClientPhone: "clientPhone",
    ClientIdNumber: "clientIdNumber",
    ClientAddress: "clientAddress",
    UnitId: "unitId",
    UnitNumber: "unitNumber",
    UnitType: "unitType",
    UnitPrice: "unitPrice",
    UnitDiscount: "unitDiscount",
    ReservationDiscount: "reservationDiscount",
    PaymentMethod: "paymentMethod",
    PackageType: "packageType",
    ReservationFee: "reservationFee",
    SalesName: "salesName",
    BrokerName: "brokerName",
    BrokerCompany: "brokerCompany",
    BrokerId: "brokerId",
  };
  const out = { ...raw };
  for (const [pascal, camel] of Object.entries(map)) {
    if (raw[pascal] !== undefined && out[camel] === undefined) {
      out[camel] = raw[pascal];
    }
  }
  // Clients array: API returns Clients (one-to-many)
  const rawClients = raw.Clients || raw.clients;
  if (Array.isArray(rawClients) && rawClients.length > 0) {
    out.clients = rawClients.map((c) => ({
      clientId: c.ClientId ?? c.clientId,
      clientName: c.ClientName ?? c.clientName,
      clientEmail: c.ClientEmail ?? c.clientEmail,
      clientPhone: c.ClientPhone ?? c.clientPhone,
      isPrimary: c.IsPrimary ?? c.isPrimary ?? false,
    }));
  } else {
    out.clients = [];
  }
  // Approvals: API returns Approvals (ReservationApprovalEntry[])
  const rawApprovals = raw.Approvals || raw.approvals;
  if (Array.isArray(rawApprovals)) {
    out.approvals = rawApprovals.map((a) => ({
      sequence: a.Sequence ?? a.sequence,
      adminName: a.AdminName ?? a.adminName ?? null,
      adminType: a.AdminType ?? a.adminType ?? null,
      approvedAt: a.ApprovedAt ?? a.approvedAt ?? null,
    }));
  } else {
    out.approvals = [];
  }
  // If API returns nested Unit object, flatten unit fields
  const unit = raw.Unit || raw.unit;
  if (unit && typeof unit === "object") {
    if (unit.UnitNumber !== undefined && out.unitNumber === undefined) out.unitNumber = unit.UnitNumber;
    if (unit.Number !== undefined && out.unitNumber === undefined) out.unitNumber = unit.Number;
    if (unit.UnitType !== undefined && out.unitType === undefined) out.unitType = unit.UnitType;
    if (unit.Type !== undefined && out.unitType === undefined) out.unitType = unit.Type;
    if (unit.UnitPrice !== undefined && out.unitPrice === undefined) out.unitPrice = unit.UnitPrice;
    if (unit.Price !== undefined && out.unitPrice === undefined) out.unitPrice = unit.Price;
    if (unit.UnitDiscount !== undefined && out.unitDiscount === undefined) out.unitDiscount = unit.UnitDiscount;
  }
  return out;
}

function Field({ label, labelAr, value }) {
  const isNode = typeof value === "object" && value !== null && React.isValidElement(value);
  return (
    <FieldRow>
      <span className="label">
        {label} <span style={{ color: "#999", fontSize: "0.78rem" }}>/ {labelAr}</span>
      </span>
      {isNode ? (
        <Box className="value" component="div" sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#222", textAlign: "right" }}>{value}</Box>
      ) : (
        <span className="value">{value || "—"}</span>
      )}
    </FieldRow>
  );
}

export default function ReservationDetails() {
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get("reservationid");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReservation = () => {
    if (!reservationId) {
      setError("No reservation ID provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getReservationById(reservationId)
      .then((res) => {
        let parsed = res;
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            // use as-is
          }
        }
        setData(normalizeReservationResponse(parsed));
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setError("Reservation not found.\nلم يتم العثور على الحجز.");
        } else {
          setError("An error occurred while loading the reservation.\nحدث خطأ أثناء تحميل بيانات الحجز.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!reservationId) {
      setError("No reservation ID provided.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getReservationById(reservationId)
      .then((res) => {
        if (cancelled) return;
        let parsed = res;
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            // use as-is
          }
        }
        setData(normalizeReservationResponse(parsed));
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError("Reservation not found.\nلم يتم العثور على الحجز.");
        } else {
          setError("An error occurred while loading the reservation.\nحدث خطأ أثناء تحميل بيانات الحجز.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [reservationId]);

  if (loading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
          <CircularProgress size={48} sx={{ color: "#1a237e" }} />
          <Typography color="#555">Loading reservation details...</Typography>
          <Typography color="#999" fontSize="0.85rem">جاري تحميل بيانات الحجز</Typography>
        </Box>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Container maxWidth="sm" sx={{ pt: 8 }}>
          <Alert severity="error" sx={{ borderRadius: 3, fontSize: "1rem", whiteSpace: "pre-line" }}>
            {error}
          </Alert>
        </Container>
      </PageWrapper>
    );
  }

  const status = STATUS_MAP[data.status] || STATUS_MAP[0];
  const unitType = UNIT_TYPE_MAP[data.unitType] || { label: "—", labelAr: "—" };

  return (
    <PageWrapper>
      <StyledPaper elevation={0}>
        {/* Header */}
        <Header>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", px: 1 }}>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 2 }}>
              <img src={Logo} alt="Logo" style={{ height: 60, marginBottom: 12 }} />
              <Typography variant="h6" fontWeight={700}>Reservation Details</Typography>
              <Typography fontSize="1rem" sx={{ opacity: 0.9 }}>تفاصيل الحجز</Typography>
            </Box>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                onClick={fetchReservation}
                sx={{ color: "rgba(255,255,255,0.9)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                title="Refresh / تحديث"
                size="small"
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>
        </Header>

        {/* Reservation Info */}
        <Section>
          <SectionTitle>
            <span>Reservation / الحجز</span>
            <Chip
              label={`${status.label} / ${status.labelAr}`}
              size="small"
              sx={{
                bgcolor: status.bg,
                color: status.color,
                fontWeight: 700,
                fontSize: "0.78rem",
                border: `1px solid ${status.color}`,
              }}
            />
          </SectionTitle>
          <Field label="Reservation #" labelAr="رقم الحجز" value={data.id} />
          <Field label="Date" labelAr="التاريخ" value={formatDate(data.reservationDate)} />
          {data.approvals && data.approvals.length > 0 ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography component="div" sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#666", mb: 1 }}>
                Approvals / الموافقات
              </Typography>
              {data.approvals.map((approval, idx) => {
                const adminTypeLabel = approval.adminType || "Admin";
                const adminTypeAr = { Operation: "تشغيل", Finance: "مالية", CustomerSupport: "خدمة العملاء" }[adminTypeLabel] || "مدير";
                const isApproved = approval.approvedAt && approval.adminName;
                return (
                  <FieldRow key={approval.sequence ?? idx} sx={{ py: 0.75 }}>
                    <span className="label">
                      {adminTypeLabel} admin <span style={{ color: "#999", fontSize: "0.78rem" }}>/ موافقة {adminTypeAr}</span>
                    </span>
                    <Box className="value" sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <Chip
                        label={isApproved ? "Approved / تمت الموافقة" : "Pending / قيد الانتظار"}
                        size="small"
                        sx={{
                          bgcolor: isApproved ? "#e8f5e9" : "#fff3e0",
                          color: isApproved ? "#2e7d32" : "#ed6c02",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          border: `1px solid ${isApproved ? "#2e7d32" : "#ed6c02"}`,
                        }}
                      />
                      {isApproved && approval.adminName && (
                        <Typography component="span" sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#222" }}>
                          {adminTypeLabel} approved: {approval.adminName}
                          {approval.approvedAt && (
                            <Typography component="span" sx={{ fontSize: "0.8rem", color: "#666", fontWeight: 500, ml: 0.5 }}>
                              ({formatDate(approval.approvedAt)})
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </FieldRow>
                );
              })}
              {data.requiredApprovalCount != null && (
                <Typography variant="caption" sx={{ color: "#999", display: "block", mt: 0.5 }}>
                  {data.approvals.filter((a) => a.approvedAt && a.adminName).length} of {data.requiredApprovalCount} required approvals
                </Typography>
              )}
            </Box>
          ) : (
            <Field
              label="Approvals"
              labelAr="الموافقات"
              value="No approval data"
            />
          )}
        </Section>

        <Divider />

        {/* Client Info (primary) */}
        <Section>
          <SectionTitle>Client (Primary) / العميل الرئيسي</SectionTitle>
          <Field label="Name" labelAr="الاسم" value={data.clientName} />
          <Field label="Phone" labelAr="الهاتف" value={data.clientPhone} />
          <Field label="Email" labelAr="البريد الإلكتروني" value={data.clientEmail} />
          <Field label="ID Number" labelAr="رقم الهوية" value={data.clientIdNumber} />
          <Field label="Address" labelAr="العنوان" value={data.clientAddress} />
        </Section>

        {/* All clients (when API returns clients array) */}
        {data.clients && data.clients.length > 0 && (
          <>
            <Divider />
            <Section>
              <SectionTitle>All Clients / العملاء</SectionTitle>
              {data.clients.map((c, idx) => (
                <Box key={c.clientId || idx} sx={{ mb: 1.5, p: 1.5, bgcolor: "#f9f9f9", borderRadius: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <span className="value">{c.clientName || "—"}</span>
                    {c.isPrimary && (
                      <Chip label="Primary" size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, fontSize: "0.7rem" }} />
                    )}
                  </Box>
                  <FieldRow><span className="label">Phone / الهاتف</span><span className="value">{c.clientPhone || "—"}</span></FieldRow>
                  <FieldRow><span className="label">Email / البريد</span><span className="value">{c.clientEmail || "—"}</span></FieldRow>
                </Box>
              ))}
            </Section>
          </>
        )}

        <Divider />

        {/* Unit Info */}
        <Section>
          <SectionTitle>Unit / الوحدة</SectionTitle>
          <Field label="Unit Number" labelAr="رقم الوحدة" value={data.unitNumber} />
          <Field label="Type" labelAr="النوع" value={`${unitType.label} / ${unitType.labelAr}`} />
          <Field label="Price" labelAr="السعر" value={formatCurrency(data.unitPrice)} />
          <Field label="Unit Discount" labelAr="خصم الوحدة" value={formatCurrency(data.unitDiscount)} />
        </Section>

        <Divider />

        {/* Payment Info */}
        <Section>
          <SectionTitle>Payment / الدفع</SectionTitle>
          <Field label="Method" labelAr="طريقة الدفع" value={data.paymentMethod} />
          <Field label="Package" labelAr="الباقة" value={data.packageType} />
          <Field label="Reservation Fee" labelAr="رسوم الحجز" value={formatCurrency(data.reservationFee)} />
          <Field label="Reservation Discount" labelAr="خصم الحجز" value={formatCurrency(data.reservationDiscount)} />
        </Section>

        <Divider />

        {/* Sales Info */}
        <Section>
          <SectionTitle>Sales / المبيعات</SectionTitle>
          <Field label="Sales Person" labelAr="مسؤول المبيعات" value={data.salesName} />
          <Field label="Broker" labelAr="الوسيط" value={data.brokerName} />
          <Field label="Broker Company" labelAr="شركة الوسيط" value={data.brokerCompany} />
        </Section>

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 2, px: 3, bgcolor: "#f5f5f5" }}>
          <Typography fontSize="0.75rem" color="#999">
            This is a read-only verification page / هذه صفحة للتحقق فقط
          </Typography>
        </Box>
      </StyledPaper>
    </PageWrapper>
  );
}
